import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { shipmentService } from '@/services/shipmentService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBox,
  faMapMarkerAlt,
  faTruck,
  faCheckCircle,
  faClock,
  faCalendarAlt,
  faSave,
  faArrowLeft,
  faSearch,
  faExclamationCircle,
  faCheck,
  faInfoCircle,
  faMapPin,
  faRoute,
  faClipboardList
} from '@fortawesome/free-solid-svg-icons';
import { getAgentTrackingStatuses } from '@/utils/formatters';
import logoImg from '@/assets/fexlogistics-logo.jpg';
import AgentReceipt from '@/components/AgentReceipt';

const inferTypeFromCode = (code) => {
  const c = String(code || '').toUpperCase();
  const idx = c.startsWith('FX') ? 2 : 0;
  const lead = c[idx];
  if (lead === '1') return 'local';
  if (lead === '2') return 'interstate';
  if (lead === '3') return 'international';
  return 'interstate';
};

const UpdateTracking = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [updateStatus, setUpdateStatus] = useState('');
  const [updateNote, setUpdateNote] = useState('');
  const [trackingCode, setTrackingCode] = useState('');
  const [shipment, setShipment] = useState(null);
  const [fullShipment, setFullShipment] = useState(null);

  useEffect(() => {
    const code = location.state?.trackingCode;
    if (code && typeof code === 'string') {
      setTrackingCode(code);
      // auto track on mount with provided code
      setTimeout(() => {
        handleTrackShipment();
      }, 0);
    }
  }, [location.state?.trackingCode]);

  const handleTrackShipment = async () => {
    if (!trackingCode) {
      setError('Please enter a tracking code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const data = await shipmentService.track(trackingCode);
      setShipment(data.shipment || null);
      if (!data.shipment) {
        setError('No shipment found for this tracking code');
      }
      const sid = data.shipment?._id;
      if (sid) {
        try {
          const token = localStorage.getItem('token');
          if (token) {
            const res = await shipmentService.getById(sid, token);
            setFullShipment(res?.shipment || null);
          }
        } catch (_) {
          setFullShipment(null);
        }
      }
    } catch (e) {
      setError(e?.message || 'Failed to track shipment');
      setShipment(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    try {
      const tl = Array.isArray(shipment?.statusHistory) ? shipment.statusHistory : [];
      const ce = tl.find((e) => e && e.completed === false) || (tl.length ? tl[tl.length - 1] : null);
      setUpdateNote(ce?.note || '');
    } catch { }
  }, [shipment]);

  const handleUpdateTracking = async () => {
    if (!shipment?._id) {
      setError('Shipment not loaded. Track by code first.');
      return;
    }
    if (!updateStatus) {
      setError('Please select a status update');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const result = await shipmentService.updateStatus(shipment._id, updateStatus, updateNote, token);
      const updated = result?.shipment || { ...shipment, status: updateStatus };
      setShipment(updated);
      setUpdateStatus('');
      setUpdateNote('');
      setError('Tracking updated successfully');
      setTimeout(() => setError(''), 3000);
    } catch (e) {
      setError(e?.message || 'Failed to update tracking');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'delivered') return 'bg-green-100 text-green-800';
    if (s === 'delayed') return 'bg-orange-100 text-orange-800';
    if (s === 'lost') return 'bg-red-100 text-red-800';
    if (s === 'cancelled') return 'bg-red-100 text-red-800';
    if (s === 'shipment departed') return 'bg-indigo-100 text-indigo-800';
    if (s === 'shipment arrival sorting facility') return 'bg-yellow-100 text-yellow-800';
    if (s === 'shipment rescheduled') return 'bg-orange-100 text-orange-800';
    if (s === 'shipment available for pickup (terminal)') return 'bg-purple-100 text-purple-800';
    if (s.startsWith("int'l")) return 'bg-teal-100 text-teal-800';
    return 'bg-blue-100 text-blue-800';
  };

  const getStatusIcon = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'delivered') return faCheckCircle;
    if (s === 'delayed') return faClock;
    if (s === 'lost') return faExclamationCircle;
    if (s === 'shipment departed') return faTruck;
    if (s === 'shipment arrival sorting facility') return faBox;
    if (s === 'shipment rescheduled') return faClock;
    if (s === 'shipment available for pickup (terminal)') return faMapMarkerAlt;
    if (s.startsWith("int'l")) return faTruck;
    return faBox;
  };

  const timeline = Array.isArray(shipment?.statusHistory) ? shipment.statusHistory : [];
  const typeHint = shipment?.serviceType || inferTypeFromCode(trackingCode || shipment?.trackingCode);
  const statusOptions = getAgentTrackingStatuses(typeHint);

  const printable = fullShipment || shipment;
  const safe = (v) => (v && typeof v === 'string' ? v : (typeof v === 'number' ? String(v) : ''));
  const shortTrackingCode = (code) => {
    const c = String(code || '').toUpperCase();
    const lead = c.startsWith('FX') ? c[2] : c[0];
    const fnvPrime = 16777619;
    let hash = 2166136261;
    for (let i = 0; i < c.length; i++) {
      hash ^= c.charCodeAt(i);
      hash = (hash * fnvPrime) >>> 0;
    }
    const num = String(hash % 1000000).padStart(6, '0');
    return `FX${lead}${num}`;
  };
  const onPrint = () => {
    window.print();
  };

  const origin = (
    shipment?.origin || shipment?.pickupLocation || shipment?.senderAddress || shipment?.senderState ||
    fullShipment?.origin || fullShipment?.pickupLocation || fullShipment?.senderAddress || fullShipment?.senderState ||
    ''
  );
  const destination = (
    shipment?.destination || shipment?.receiverCity || shipment?.receiverState || shipment?.country || shipment?.dropoffLocation ||
    fullShipment?.destination || fullShipment?.receiverCity || fullShipment?.receiverState || fullShipment?.country || fullShipment?.dropoffLocation ||
    ''
  );
  const itemType = (() => {
    const d = (printable?.deliveryType || '').toString();
    if (d) return d.charAt(0).toUpperCase() + d.slice(1);
    if ((printable?.serviceType || typeHint) === 'international' && printable?.internationalSubtype) {
      const s = printable.internationalSubtype.toString();
      return s.charAt(0).toUpperCase() + s.slice(1);
    }
    const t = (printable?.serviceType || typeHint || '').toString();
    return t ? t.charAt(0).toUpperCase() + t.slice(1) : '';
  })();
  const eta = printable?.estimatedDelivery || '';
  const currentEvent = timeline.find((e) => e && e.completed === false) || (timeline.length ? timeline[timeline.length - 1] : null);
  const currentNote = currentEvent?.note || '';

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Back to Dashboard */}
        <div className="mb-6 print:hidden">
          <Link
            to="/dashboard/agent"
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
            Back to Dashboard
          </Link>
        </div>

        {/* Header */}
        <div className="mb-8 text-center print:hidden">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Update Tracking</h1>
          <p className="text-gray-600">Update tracking information for shipments</p>
        </div>

        {/* Track by Code */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8 print:hidden">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Enter Tracking Code</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="e.g., FX2xxxxx"
                value={trackingCode}
                onChange={(e) => setTrackingCode(e.target.value)}
              />
            </div>
          </div>

          <button
            onClick={handleTrackShipment}
            disabled={loading || !trackingCode}
            className="w-full sm:w-auto px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors duration-200"
          >
            {loading ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Loading...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faBox} className="mr-2" />
                Track Shipment
              </>
            )}
          </button>

          {error && (
            <div
              className={`mt-4 p-4 rounded-lg text-sm flex items-start ${error.includes('success')
                ? 'bg-green-50 border border-green-200 text-green-700'
                : 'bg-red-50 border border-red-200 text-red-700'
                }`}
            >
              <FontAwesomeIcon
                icon={error.includes('success') ? faCheck : faExclamationCircle}
                className={`mr-2 mt-0.5 ${error.includes('success') ? 'text-green-500' : 'text-red-500'}`}
              />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Tracking Results */}
        {shipment && (
          <div className="space-y-6">
            {/* Shipment Status Card */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden print:hidden">
              <div className="p-6 border-b border-gray-200">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-900">Shipment Status</h2>
                  <div className="flex items-center mt-2 md:mt-0">
                    <div className={`p-2 rounded-full mr-2 ${shipment.status === 'Delivered' ? 'bg-green-100' :
                      shipment.status === 'Out for Delivery' ? 'bg-purple-100' :
                        shipment.status === 'In Transit' ? 'bg-yellow-100' :
                          shipment.status === 'Delayed' ? 'bg-orange-100' :
                            shipment.status === 'Lost' ? 'bg-red-100' : 'bg-blue-100'
                      }`}>
                      <FontAwesomeIcon
                        icon={getStatusIcon(shipment.status)}
                        className={`${shipment.status === 'Delivered' ? 'text-green-500' :
                          shipment.status === 'Out for Delivery' ? 'text-purple-500' :
                            shipment.status === 'In Transit' ? 'text-yellow-500' :
                              shipment.status === 'Delayed' ? 'text-orange-500' :
                                shipment.status === 'Lost' ? 'text-red-500' : 'text-blue-500'
                          }`}
                      />
                    </div>
                    <span
                      className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(
                        shipment.status
                      )}`}
                    >
                      {shipment.status}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Tracking Number</p>
                    <p className="font-medium text-gray-900">{shipment.trackingCode}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Service Type</p>
                    <p className="font-medium text-gray-900 capitalize">{itemType || '—'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Origin</p>
                    <p className="font-medium text-gray-900">{origin}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Destination</p>
                    <p className="font-medium text-gray-900">{destination}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Estimated Delivery</p>
                    <p className="font-medium text-gray-900">{eta}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Current Status Detail</p>
                    <p className="font-medium text-gray-900">{currentNote || '—'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Update Tracking Form */}
            <div className="bg-white rounded-xl shadow-sm p-6 print:hidden">
              <div className="flex items-center mb-4">
                <FontAwesomeIcon icon={faClipboardList} className="text-red-500 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Update Tracking</h3>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    className="w-full md:w-64 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    value={updateStatus}
                    onChange={(e) => setUpdateStatus(e.target.value)}
                  >
                    <option value="">Select Status</option>
                    {statusOptions.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Status Detail</label>
                  <textarea
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    rows="3"
                    placeholder="Add detail for the current status"
                    value={updateNote}
                    onChange={(e) => setUpdateNote(e.target.value)}
                  ></textarea>
                </div>

                <button
                  onClick={handleUpdateTracking}
                  disabled={loading}
                  className="w-full sm:w-auto px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors duration-200"
                >
                  {loading ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Updating...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faSave} className="mr-2" />
                      Update Tracking
                    </>
                  )}
                </button>
                <button
                  onClick={onPrint}
                  className="w-full sm:w-auto ml-0 sm:ml-3 px-6 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-900 flex items-center justify-center transition-colors duration-200"
                >
                  Print Receipt
                </button>
              </div>
            </div>

            {/* Tracking Timeline */}
            <div className="bg-white rounded-xl shadow-sm p-6 print:hidden">
              <div className="flex items-center mb-6">
                <FontAwesomeIcon icon={faRoute} className="text-red-500 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Tracking Timeline</h3>
              </div>

              {timeline.length === 0 ? (
                <div className="text-center py-8">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 text-gray-400 mb-3">
                    <FontAwesomeIcon icon={faInfoCircle} />
                  </div>
                  <p className="text-gray-500">No tracking history available</p>
                </div>
              ) : (
                <div className="relative">
                  {/* Vertical line */}
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>

                  <div className="space-y-6">
                    {timeline.map((event, index) => (
                      <div key={index} className="relative flex items-start">
                        {/* Timeline dot */}
                        <div
                          className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center z-10 ${event.completed ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
                            }`}
                        >
                          <FontAwesomeIcon icon={getStatusIcon(event.status)} className="text-sm" />
                        </div>

                        {/* Timeline content */}
                        <div className="ml-6 min-w-0 flex-1 pb-6">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-1">
                            <p className="text-sm font-medium text-gray-900">{event.status}</p>
                            {!event.completed && (
                              <span className="mt-1 sm:mt-0 px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                                Current
                              </span>
                            )}
                          </div>

                          <div className="flex items-center text-sm text-gray-500 mb-1">
                            <FontAwesomeIcon icon={faCalendarAlt} className="mr-1" />
                            {event.date} {event.time ? `at ${event.time}` : ''}
                          </div>

                          {event.location && (
                            <div className="flex items-start text-sm text-gray-500 mb-1">
                              <FontAwesomeIcon icon={faMapPin} className="mr-1 mt-0.5" />
                              <span>{event.location}</span>
                            </div>
                          )}

                          {event.note && (
                            <div className="mt-2 p-3 bg-gray-50 rounded-lg text-sm text-gray-700">
                              <FontAwesomeIcon icon={faInfoCircle} className="text-gray-400 mr-1" />
                              {event.note}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <AgentReceipt shipment={printable} typeHint={typeHint} />
          </div>
        )}
      </div>
    </div>
  );
};

export default UpdateTracking;
